import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import bcrypt from 'bcryptjs';

import db from '../../db/db';
import { setAlert } from '../../actions/alert';
import pkg from '../../../package.json';

const version = pkg.version;
const catalogos = ['empleados', 'productos', 'metodos', 'sucursales', 'proveedores', 'eventos'];

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({ usuario: '', password: '' });
    const [errorLogin, setErrorLogin] = useState(null);
    const [statusLogin, setStatusLogin] = useState(false);
    const [statusSync, setStatusSync] = useState(false);
    const [sincronizado, setSincronizado] = useState(false);

    // Selección de sucursal (multi-sucursal)
    const [modalSucursal, setModalSucursal] = useState(false);
    const [sucursalesDisponibles, setSucursalesDisponibles] = useState([]);
    const [empleadoPendiente, setEmpleadoPendiente] = useState(null);

    const { usuario, password } = formData;

    const onChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errorLogin) setErrorLogin(null);
    };

    const handleLogin = async e => {
        e.preventDefault();
        setStatusLogin(true);
        setErrorLogin(null);

        try {
            const empleado = await db.empleados
                .where('usuario')
                .equals(usuario.trim())
                .first();

            if (!empleado) {
                setErrorLogin('Usuario o contraseña incorrectos');
                setStatusLogin(false);
                return;
            }

            const passwordValido = await bcrypt.compare(password, empleado.password);

            if (!passwordValido) {
                setErrorLogin('Usuario o contraseña incorrectos');
                setStatusLogin(false);
                return;
            }

            if (empleado.tipo !== 0 && empleado.tipo !== 5) {
                setErrorLogin('Acceso restringido. No tienes permiso para usar este sistema.');
                setStatusLogin(false);
                return;
            }

            // Resolver sucursales disponibles para este empleado
            // El API devuelve `sucursales` (array de objetos) cuando tiene más de una
            const sucursalesEmpleado = Array.isArray(empleado.sucursales) && empleado.sucursales.length > 0
                ? empleado.sucursales
                : empleado.sucursal
                    ? [{ _id: empleado.sucursal?._id || empleado.sucursal, nombre: empleado.sucursal?.nombre || '' }]
                    : [];

            if (sucursalesEmpleado.length === 0) {
                setErrorLogin('Tu cuenta no tiene una sucursal asignada. Contacta al administrador.');
                setStatusLogin(false);
                return;
            }

            if (sucursalesEmpleado.length === 1) {
                // Una sola sucursal → iniciar sesión directo
                await db.sesion.clear();
                await db.sesion.add({ ...empleado, sucursal: sucursalesEmpleado[0] });
                navigate('/dashboard');
            } else {
                // Varias sucursales → mostrar modal de selección
                setEmpleadoPendiente(empleado);
                setSucursalesDisponibles(sucursalesEmpleado);
                setStatusLogin(false);
                setModalSucursal(true);
            }
        } catch {
            setErrorLogin('Usuario o contraseña incorrectos');
            setStatusLogin(false);
        }
    };

    const handleConfirmarSucursal = async (sucursal) => {
        await db.sesion.clear();
        await db.sesion.add({ ...empleadoPendiente, sucursal });
        navigate('/dashboard');
    };

    const handleSync = async () => {
        setStatusSync(true);

        try {
            const res = await axios.get(`${process.env.REACT_APP_API_ROUTE}/sincronizacion/descargar`);
            const data = res.data;

            await Promise.all(
                catalogos.map(async col => {
                    if (data[col]) {
                        await db[col].clear();
                        await db[col].bulkAdd(data[col]);
                    }
                })
            );

            setSincronizado(true);
            dispatch(setAlert('Información sincronizada correctamente', 'success'));
        } catch {
            dispatch(setAlert('Error al sincronizar. Verifique la conexión.', 'danger'));
        } finally {
            setStatusSync(false);
        }
    };

    return (
        <>
        {modalSucursal && (
            <div style={modal.overlay}>
                <div style={modal.card}>
                    <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                        <div style={modal.iconWrap}>
                            <i className="fas fa-store" style={{ color: '#F5C800', fontSize: '1.4rem' }}></i>
                        </div>
                        <h5 style={modal.titulo}>¿En qué sucursal estás trabajando hoy?</h5>
                        <p style={modal.subtitulo}>Selecciona la sucursal para esta sesión</p>
                    </div>
                    <div style={modal.lista}>
                        {sucursalesDisponibles.map((s, i) => (
                            <button
                                key={s._id || s.id || i}
                                style={modal.btnSucursal}
                                onClick={() => handleConfirmarSucursal(s)}
                            >
                                <i className="fas fa-map-marker-alt me-3" style={{ color: '#F5C800' }}></i>
                                {s.nombre || `Sucursal ${i + 1}`}
                                <i className="fas fa-chevron-right ms-auto" style={{ color: '#555' }}></i>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        )}
        <div style={styles.page}>
            <div style={styles.card} className="shadow-lg">

                {/* Header */}
                <div style={styles.header}>
                    <img
                        src="/assets/images/logo_blanco.png"
                        alt="Papa Roll"
                        style={{ height: '90px', width: 'auto', margin: '0 auto 0.5rem', display: 'block' }}
                    />
                    <p style={styles.subtitle}>Punto de Venta</p>
                </div>

                {/* Form */}
                <div style={styles.body}>
                    <form onSubmit={handleLogin} noValidate>

                        <div className="mb-4">
                            <label style={styles.label}>
                                <i className="fas fa-user me-2 text-secondary"></i>
                                Usuario
                            </label>
                            <input
                                type="text"
                                name="usuario"
                                value={usuario}
                                onChange={onChange}
                                className={`form-control form-control-lg${errorLogin ? ' is-invalid' : ''}`}
                                style={styles.input}
                                placeholder="Ingresa tu usuario"
                                autoComplete="username"
                                autoCapitalize="none"
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label style={styles.label}>
                                <i className="fas fa-lock me-2 text-secondary"></i>
                                Contraseña
                            </label>
                            <input
                                type="password"
                                name="password"
                                value={password}
                                onChange={onChange}
                                className={`form-control form-control-lg${errorLogin ? ' is-invalid' : ''}`}
                                style={styles.input}
                                placeholder="Ingresa tu contraseña"
                                autoComplete="current-password"
                                required
                            />
                            {errorLogin && (
                                <div style={styles.errorMsg}>
                                    <i className="fas fa-exclamation-circle me-2"></i>
                                    {errorLogin}
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="btn btn-lg w-100"
                            style={styles.btnLogin}
                            disabled={statusLogin || !usuario || !password || !sincronizado}
                        >
                            {statusLogin ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                                    Ingresando...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-sign-in-alt me-2"></i>
                                    Iniciar sesión
                                </>
                            )}
                        </button>
                    </form>

                    <hr style={styles.divider} />

                    <button
                        type="button"
                        className="btn btn-lg w-100"
                        style={styles.btnSync}
                        onClick={handleSync}
                        disabled={statusSync}
                    >
                        {statusSync ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                                Sincronizando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-sync-alt me-2"></i>
                                Sincronizar información
                            </>
                        )}
                    </button>

                    <p style={styles.syncHint}>
                        Sincroniza antes de trabajar sin internet
                    </p>
                    <p style={styles.versionTag}>v{version}</p>
                </div>
            </div>
        </div>
        </>
    );
};

const styles = {
    page: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
        padding: '1.5rem',
    },
    card: {
        width: '100%',
        maxWidth: '440px',
        borderRadius: '20px',
        overflow: 'hidden',
        background: '#1A1A1A',
        boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
    },
    header: {
        background: '#111111',
        padding: '2.5rem 2rem 2rem',
        textAlign: 'center',
    },
    subtitle: {
        color: '#888888',
        margin: '0.25rem 0 0',
        fontSize: '0.95rem',
        textTransform: 'uppercase',
        letterSpacing: '2px',
    },
    body: {
        padding: '2rem',
    },
    label: {
        display: 'block',
        fontWeight: 600,
        color: '#F0F0F0',
        marginBottom: '0.5rem',
        fontSize: '0.95rem',
    },
    input: {
        borderRadius: '10px',
        border: '2px solid #2E2E2E',
        padding: '0.85rem 1rem',
        fontSize: '1.05rem',
        background: '#1F1F1F',
        color: '#F0F0F0',
        transition: 'border-color 0.2s, box-shadow 0.2s',
    },
    errorMsg: {
        marginTop: '0.5rem',
        color: '#E53E3E',
        fontSize: '0.9rem',
        fontWeight: 500,
    },
    btnLogin: {
        background: '#F5C800',
        color: '#111111',
        border: 'none',
        borderRadius: '10px',
        padding: '0.9rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        letterSpacing: '0.3px',
        transition: 'background 0.2s, box-shadow 0.2s',
    },
    divider: {
        margin: '1.5rem 0',
        borderColor: '#2E2E2E',
    },
    btnSync: {
        background: 'transparent',
        color: '#888888',
        border: '1.5px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.85rem',
        fontWeight: 600,
        fontSize: '1rem',
        transition: 'border-color 0.2s, color 0.2s',
    },
    syncHint: {
        textAlign: 'center',
        color: '#555555',
        fontSize: '0.8rem',
        marginTop: '0.75rem',
        marginBottom: 0,
    },
    versionTag: {
        color: '#333',
        fontSize: '0.75rem',
        textAlign: 'center',
        marginTop: '1rem',
        marginBottom: 0,
    },
};

const modal = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1.5rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '20px',
        padding: '2rem',
        width: '100%',
        maxWidth: '400px',
    },
    iconWrap: {
        width: '60px',
        height: '60px',
        background: 'rgba(245,200,0,0.1)',
        border: '1px solid rgba(245,200,0,0.25)',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 1.25rem',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        margin: '0 0 0.35rem',
        fontSize: '1.1rem',
    },
    subtitulo: {
        color: '#666',
        fontSize: '0.88rem',
        margin: 0,
    },
    lista: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.65rem',
    },
    btnSucursal: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        background: '#111',
        border: '1px solid #2E2E2E',
        borderRadius: '12px',
        padding: '1rem 1.25rem',
        color: '#C0C0C0',
        fontWeight: 600,
        fontSize: '1rem',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'border-color 0.15s, background 0.15s',
    },
};

export default Login;
